import React, { useState, useEffect } from 'react';
import {
  shape, string, func, arrayOf, bool,
} from 'prop-types';
import { Profile as ProfilePage, Loading as LoadingPage } from '@hyphengroup/cag-components';
import { withTracking, userProfileLoginDashboardSaveProfileChangesEvent, LOCATIONS } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import Layout from 'components/layout';
import { CustomerProfileContext } from 'containers/withCustomerProfileContext';
import {
  withUpdateCustomer,
  withRequestPasswordReset,
  getDashboardProfileQueryVariables,
  withDashboardProfilePage,
} from 'containers/withQueries';
import {
  getStaticText, getImages,
  getDashboardDesktopMenuBar,
} from 'helpers/Utils';
import { compose } from 'react-apollo';
import Queries from 'queries';
import usePageReloadOnHistoryNavigation from 'hooks/usePageReloadOnHistoryNavigation';
import { locations as AUTH_LOCATIONS } from 'containers/withAuth';

const market = process.env.RAZZLE_MARKET;

function DashboardProfile({
  updateCustomerMutation,
  requestPasswordResetMutation,
  defaultLanguage,
  localisation,
  match,
  track,
  contentstackKeys,
  dashboardQueryProfile,
}) {
  const { params } = match;
  const language = params?.language || defaultLanguage;
  const [processing, setProcessing] = useState(false);
  const [banner, setBanner] = useState({});
  const [pageLoading] = usePageReloadOnHistoryNavigation();

  useEffect(() => {
    track({ data: { location: LOCATIONS.DASHBOARD } });
  }, []);

  if (!dashboardQueryProfile || dashboardQueryProfile.loading || pageLoading) {
    return <LoadingPage />;
  }

  const {
    pageConfig: { footer, header }, customerProfile, seoMetadata, refetch,
  } = dashboardQueryProfile;
  const { logo } = header;
  const staticText = getStaticText(dashboardQueryProfile);
  const images = getImages(dashboardQueryProfile);
  const {
    phoneNumber, email, firstName, familyName,
  } = customerProfile?.customer || {};

  const dashboarProfileQueryVariables = getDashboardProfileQueryVariables({
    localisation,
    contentstackKeys,
    defaultLanguage,
    match,
  });

  function handleProfileSubmit(userData) {
    setProcessing(true);

    updateCustomerMutation({
      variables: {
        language: localisation[language],
        customer: {
          firstName: userData?.firstName,
          familyName: userData?.lastName,
          ...(userData?.phoneNumber && {
            phoneNumber: userData?.phoneNumber,
          }),
        },
      },
      refetchQueries: [
        {
          query: Queries.GetDashboardProfile,
          variables: dashboarProfileQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
    }).then(() => {
      setProcessing(false);
      setBanner({
        isShown: true,
        text: staticText.PROFILE_SUCCESSFULLY_CHANGED_MESSAGE,
        type: 'success',
      });
      userProfileLoginDashboardSaveProfileChangesEvent(track, 'success');
    })
      .catch((error) => {
        setProcessing(false);
        userProfileLoginDashboardSaveProfileChangesEvent(track, 'failure');
        throw new Error(error);
      });
  }

  function handlePasswordEdit(showSignup) {
    if (email) {
      requestPasswordResetMutation({
        variables: {
          language: localisation[language],
          email,
        },
      })
        .catch((error) => {
          throw new Error(error);
        });
      showSignup({
        authLocation: AUTH_LOCATIONS.EMAIL_VERIFICATION,
      });
    }
  }

  return (
    <Layout
      footer={footer}
      header={header}
      isNavPanelHidden
      onlyForLoggedInUser
      defaultAuthLocation="EMAIL_VERIFICATION"
      variant="fullScreen"
      seoMetadata={seoMetadata}
      customerProfile={customerProfile}
      staticText={staticText}
      images={images}
      refetch={refetch}
    >
      <CustomerProfileContext.Consumer>
        {({ showMobileMenu, showAccountMenu, showSignup }) => (
          <div>
            <ProfilePage
              staticText={staticText}
              title={staticText.PROFILE}
              image={images?.EMAIL_SIGNUP_HERO?.image?.url}
              initialFirstName={firstName}
              initialLastName={familyName}
              initialPhone={phoneNumber?.phoneNumber || ''}
              initialEmail={email}
              market={market}
              handlers={{
                onPasswordEdit: () => handlePasswordEdit(showSignup),
                onSave: handleProfileSubmit,
                onAccountIconClick: showAccountMenu,
                onLeftIconClick: showMobileMenu,
              }}
              siteLogo={{
                link: logo?.link?.href,
                imageTitle: logo?.title,
                imageUrl: footer?.logo?.image?.url,
              }}
              loading={processing}
              desktopMenuBar={getDashboardDesktopMenuBar(staticText)}
              firstNamePlaceholder={
                      staticText.PROFILE_FIRSTNAME_PLACEHOLDER !== 'PROFILE_FIRSTNAME_PLACEHOLDER'
                        ? staticText.PROFILE_FIRSTNAME_PLACEHOLDER
                        : null
                    }
              banner={{
                ...banner,
                onClose: () => setBanner({}),
              }}
              lastNamePlaceholder={
                      staticText.PROFILE_LASTNAME_PLACEHOLDER !== 'PROFILE_LASTNAME_PLACEHOLDER'
                        ? staticText.PROFILE_LASTNAME_PLACEHOLDER
                        : null
                    }
              allowChangePhoneNumber={true}
              hidePassword={true}
              hideContactDescription={true}
            />
          </div>
        )}
      </CustomerProfileContext.Consumer>
    </Layout>
  );
}

DashboardProfile.defaultProps = {
  track: null,
};

DashboardProfile.propTypes = {
  updateCustomerMutation: func.isRequired,
  requestPasswordResetMutation: func.isRequired,
  defaultLanguage: string.isRequired,
  localisation: shape({}).isRequired,
  match: shape({
    params: shape({
      language: string,
    }),
  }).isRequired,
  contentstackKeys: shape({
    staticTextKeys: shape({
      dashboardProfile: arrayOf(string).isRequired,
    }).isRequired,
    imageAndLink: shape({
      dashboardProfile: arrayOf(string).isRequired,
    }).isRequired,
  }).isRequired,
  track: func,
  dashboardQueryProfile: shape({
    loading: bool.isRequired,
    pageConfig: shape({
      footer: shape({
        logo: shape({
          image: shape({
            url: string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      header: shape({
        logo: shape({
          link: shape({
            href: string.isRequired,
          }).isRequired,
          title: string.isRequired,
        }).isRequired,
      }).isRequired,
    }),
  }).isRequired,
};

export default withApplicationConfig(
  compose(
    withTracking,
    withUpdateCustomer,
    withRequestPasswordReset,
  )(withDashboardProfilePage(DashboardProfile)), ['languages', 'contentstackKeys']
);
